import { fromCents, parseCurrency, toCents } from 'components/Widget/shared/currency';
import { parsePercent } from 'components/Widget/shared/percent';
import { DEFAULT_EXTRA_PAYMENT_RATE, getTermUnit, MAX_MONTHS, MONTHS_PER_YEAR } from './constants';
import type { LoanBalancePoint, LoanInput, LoanResult, LoanScenario } from './types';

const PERCENT_DIVISOR = 100;

/** Rounded up: a payment rounded down can fall short of the month's interest and never amortize. */
const scheduledPaymentInCents = (principalInCents: number, monthlyRate: number, termMonths: number): number => {
  if (monthlyRate === 0) return Math.ceil(principalInCents / termMonths);

  const growth = (1 + monthlyRate) ** termMonths;

  return Math.ceil((principalInCents * monthlyRate * growth) / (growth - 1));
};

/** Carries the cent total alongside the scenario so savings subtract on integers, never on floats. */
interface AmortizedScenario {
  scenario: LoanScenario;
  totalInterestInCents: number;
}

const amortize = (
  principalInCents: number,
  monthlyRate: number,
  paymentInCents: number,
  extraInCents: number,
  termMonths: number,
): AmortizedScenario => {
  const balanceSeries: LoanBalancePoint[] = [{ elapsedMonths: 0, remainingBalance: fromCents(principalInCents) }];
  let balanceInCents = principalInCents;
  let totalInterestInCents = 0;
  let elapsedMonths = 0;

  while (balanceInCents > 0) {
    // Lenders round each month's interest to the cent before applying it, so the series is reproducible.
    const interestInCents = Math.round(balanceInCents * monthlyRate);
    const dueInCents = balanceInCents + interestInCents;
    /**
     * Rounding each month's interest to the cent leaves the scheduled payment a few cents short of
     * the balance about 1% of the time, which would bill a 60-month loan for a 61st month. The
     * closing month settles whatever is due instead, so the payoff honours the requested term.
     * An extra payment is meant to finish early, so only the baseline is held to the term.
     */
    const isClosingPayment = extraInCents === 0 && elapsedMonths + 1 >= termMonths;
    // The last payment is a stub: whatever is left, rather than a full scheduled payment.
    const appliedInCents = isClosingPayment ? dueInCents : Math.min(paymentInCents + extraInCents, dueInCents);

    if (!isClosingPayment && appliedInCents <= interestInCents) {
      throw new Error(`Payment ${appliedInCents} never covers interest ${interestInCents}; the loan cannot amortize`);
    }

    balanceInCents = dueInCents - appliedInCents;
    totalInterestInCents += interestInCents;
    elapsedMonths += 1;
    balanceSeries.push({ elapsedMonths, remainingBalance: fromCents(balanceInCents) });

    if (elapsedMonths > MAX_MONTHS) throw new Error(`Loan did not amortize within ${MAX_MONTHS} months`);
  }

  const totalInterest = fromCents(totalInterestInCents);

  return {
    scenario: {
      monthlyPayment: fromCents(paymentInCents),
      totalInterest,
      monthsToPayoff: elapsedMonths,
      // Summed in cents, then converted once: adding two converted floats drifts off the cent.
      totalCost: fromCents(principalInCents + totalInterestInCents),
      balanceSeries,
    },
    totalInterestInCents,
  };
};

export const calculateLoan = (input: LoanInput): LoanResult => {
  const principalInCents = toCents(parseCurrency(input.loanAmount));
  // Both rate types drive the same amortization; only what they already account for differs.
  const monthlyRate = parsePercent(input.rate) / PERCENT_DIVISOR / MONTHS_PER_YEAR;
  const termMonths = Math.round(parseCurrency(input.term) * getTermUnit(input.termUnit).monthsPerUnit);

  if (termMonths < 1) throw new Error(`Loan term must be at least one month: ${input.term} ${input.termUnit}`);

  const paymentInCents = scheduledPaymentInCents(principalInCents, monthlyRate, termMonths);
  const enteredExtraInCents = toCents(parseCurrency(input.extraPayment));
  const isExtraPaymentFromUser = input.extraPayment.trim() !== '' && enteredExtraInCents > 0;
  // The teaser needs a scenario to quote even before the user asks for one, so it is always modelled.
  const extraInCents = isExtraPaymentFromUser
    ? enteredExtraInCents
    : Math.round(paymentInCents * DEFAULT_EXTRA_PAYMENT_RATE);

  const baseline = amortize(principalInCents, monthlyRate, paymentInCents, 0, termMonths);
  const withExtra = amortize(principalInCents, monthlyRate, paymentInCents, extraInCents, termMonths);

  return {
    baseline: baseline.scenario,
    withExtra: withExtra.scenario,
    isExtraPaymentFromUser,
    termMonths,
    extraPayment: fromCents(extraInCents),
    amountFinanced: fromCents(principalInCents),
    interestSaved: fromCents(baseline.totalInterestInCents - withExtra.totalInterestInCents),
    monthsSaved: baseline.scenario.monthsToPayoff - withExtra.scenario.monthsToPayoff,
  };
};
